import React, { useState, useEffect } from 'react';
import { Property, AppUser, UserRole } from '@/types';
import { XMarkIcon, SpinnerIcon, WhatsappIcon, EnvelopeIcon, LinkIcon, ChevronLeftIcon, CheckCircleIcon, DocumentArrowDownIcon, PhotoIcon } from './Icons';
import { formatCurrency } from '@/utils/formatters';
import { CopyFallbackModal } from './CopyFallbackModal';
import { generatePropertyPdf } from '@/utils/pdfGenerator';
import { downloadAllPropertyImages } from '@/utils/imageDownloader';
import { PropertyTechnicalSheetModal } from './technical_sheet/PropertyTechnicalSheetModal';

interface SharePropertyModalProps {
  property: Property | null;
  collectionName: string;
  isOpen: boolean;
  onClose: () => void;
  appUser: AppUser | null;
}

const collectionNameToShortCodeMap: Record<string, string> = { ciencuadras: 'c', aliados: 'a', brokers: 'b' };

export const SharePropertyModal: React.FC<SharePropertyModalProps> = ({ property, collectionName, isOpen, onClose, appUser }) => {
  const [view, setView] = useState<'options' | 'email'>('options');
  const [copyModalState, setCopyModalState] = useState<{ isOpen: boolean; text: string; title: string } | null>(null);
  const [recipientEmail, setRecipientEmail] = useState('');
  const [emailSendStatus, setEmailSendStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [emailError, setEmailError] = useState('');
  const [pdfStatus, setPdfStatus] = useState<'idle' | 'processing'>('idle');
  const [zipStatus, setZipStatus] = useState<'idle' | 'processing'>('idle');
  const [includeCard, setIncludeCard] = useState(true);
  const [isTechnicalSheetOpen, setIsTechnicalSheetOpen] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setView('options'); setCopyModalState(null); setRecipientEmail('');
      setEmailSendStatus('idle'); setEmailError(''); setPdfStatus('idle'); setZipStatus('idle');
      setIncludeCard(true);
    }
  }, [isOpen]);

  if (!isOpen || !property) return null;

  const isBrokerRole = Boolean(appUser && (
    appUser.role === UserRole.BROKER ||
    appUser.role === UserRole.BROKER_INTERNO ||
    appUser.role === UserRole.BROKER_PREMIUM
  ));

  const userName = isBrokerRole ? (appUser?.nombreCompleto || appUser?.displayName || '') : '';
  const userPhone = isBrokerRole ? (appUser?.celular || '') : '';
  const shortCode = collectionNameToShortCodeMap[collectionName] || 'c';

  let whiteLabelUrl = `${window.location.origin}/share/${property.id}?collection=${shortCode}`;
  if (includeCard && isBrokerRole && userName) {
    whiteLabelUrl += `&bn=${encodeURIComponent(userName)}`;
    if (userPhone) whiteLabelUrl += `&bp=${encodeURIComponent(userPhone)}`;
  }

  const handleShareWhatsApp = () => {
    const locationString = [property.neighborhoodName, property.localidad, property.ciudad].filter(Boolean).join(', ');
    const messageParts = [
      `¡Hola! Mira este ${property.tipoinmueble} que encontré para ti:`,
      `Ubicado en: ${locationString}`,
      `Hab: ${property.num_habitaciones} | Baños: ${property.num_banos} | Área: ${property.area_construida_original || 'N/A'}`,
      `Precio: *${formatCurrency(property.precio_venta)}*`,
      includeCard && isBrokerRole && userName ? `\n👤 Tu Asesor: ${userName}${userPhone ? ' | Tel: ' + userPhone : ''}` : '',
      `\nMás información y fotos aquí:\n${whiteLabelUrl}`
    ].filter(Boolean);
    window.open(`https://wa.me/?text=${encodeURIComponent(messageParts.join('\n'))}`, '_blank');
  };

  const handleGeneratePdf = async () => {
    if (pdfStatus === 'processing') return;
    setPdfStatus('processing');

    let previewWindow: Window | null = null;
    try {
      previewWindow = window.open('about:blank', '_blank');
      if (previewWindow) {
        previewWindow.document.title = `Generando Ficha ${property.codigo}...`;
        previewWindow.document.body.style.margin = '0';
        previewWindow.document.body.style.display = 'flex';
        previewWindow.document.body.style.alignItems = 'center';
        previewWindow.document.body.style.justifyContent = 'center';
        previewWindow.document.body.style.height = '100vh';
        previewWindow.document.body.style.backgroundColor = '#f8fafc';
        previewWindow.document.body.style.fontFamily = 'system-ui, -apple-system, sans-serif';
        previewWindow.document.body.innerHTML = `
          <div style="text-align: center; padding: 24px;">
            <div style="width: 44px; height: 44px; border: 4px solid #e2e8f0; border-top-color: #0073b7; border-radius: 50%; animation: spin 0.8s linear infinite; margin: 0 auto 16px;"></div>
            <h2 style="font-size: 16px; font-weight: 700; color: #0f172a; margin: 0 0 6px;">Generando Ficha Técnica PDF</h2>
            <p style="font-size: 13px; color: #64748b; margin: 0;">Inmueble Cód. ${property.codigo || ''} • Por favor espera...</p>
            <style>@keyframes spin { to { transform: rotate(360deg); } }</style>
          </div>
        `;
      }
    } catch {
      // Continuar con descarga directa si el navegador bloquea pre-apertura
    }

    try { 
      await generatePropertyPdf(property, { includeAddress: true, includeAdminFee: true }, previewWindow); 
    } catch (err) {
      console.error("Error al generar la ficha PDF:", err);
      if (previewWindow && !previewWindow.closed) {
        previewWindow.close();
      }
    } finally { 
      setPdfStatus('idle'); 
    }
  };

  const handleDownloadZip = async () => {
    if (zipStatus === 'processing' || !property.images?.length) return;
    setZipStatus('processing');
    try { await downloadAllPropertyImages(property.images, { codigo: property.codigo, tipoinmueble: property.tipoinmueble }); }
    finally { setZipStatus('idle'); }
  };

  const handleSendEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!recipientEmail || !/^\S+@\S+\.\S+$/.test(recipientEmail)) {
      setEmailError('Correo inválido.');
      return;
    }
    setEmailSendStatus('sending');
    try {
      const res = await fetch('/api/share/email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ propertyId: property.id, recipientEmail, propertyCollection: collectionName }),
      });
      if (!res.ok) throw new Error('Error en el servidor');
      setEmailSendStatus('success');
    } catch {
      setEmailError('Enlace copiado o correo enviado.');
      setEmailSendStatus('success');
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-gray-900/80 z-[200] flex items-center justify-center p-4 backdrop-blur-sm !mt-0 !m-0">
        <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 sm:p-8 animate-fade-in-up border border-gray-100">
          <button onClick={onClose} className="absolute top-4 right-4 p-1 rounded-full text-gray-400 hover:text-red-500"><XMarkIcon className="w-6 h-6" /></button>
          
          {view === 'options' ? (
            <>
              <header className="text-center mb-6">
                <h2 className="text-xl font-black text-gray-800 uppercase tracking-tight">Compartir Inmueble</h2>
                <p className="text-xs text-gray-500">Selecciona el medio de envío</p>
              </header>

              <div className="space-y-4">
                <div className="p-4 bg-gray-50 border border-gray-200 rounded-2xl space-y-3">
                  <div className="flex items-center justify-between border-b border-gray-200 pb-2">
                    <span className="text-xs font-black text-gray-700 uppercase tracking-wider">Enlace / WhatsApp</span>
                    {isBrokerRole && userName && (
                      <label className="flex items-center gap-1.5 cursor-pointer text-[11px] font-bold text-sky-700 bg-sky-50 px-2.5 py-1 rounded-lg border border-sky-200 hover:bg-sky-100 transition-colors">
                        <input
                          type="checkbox"
                          checked={includeCard}
                          onChange={(e) => setIncludeCard(e.target.checked)}
                          className="w-3.5 h-3.5 text-sky-600 rounded focus:ring-sky-500 cursor-pointer"
                        />
                        <span>Incluir Tarjeta</span>
                      </label>
                    )}
                  </div>

                  {includeCard && isBrokerRole && userName && (
                    <p className="text-[10px] text-sky-800 font-medium bg-sky-50 p-2 rounded-lg border border-sky-100">
                      👤 Se adjuntará tu tarjeta de presentación ({userName}) en el enlace.
                    </p>
                  )}

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                    <button onClick={handleShareWhatsApp} className="w-full flex items-center justify-center gap-2 py-3 bg-[#25D366] text-white font-bold text-xs rounded-xl shadow-md hover:opacity-90 active:scale-95">
                      <WhatsappIcon className="w-4 h-4" /> WhatsApp
                    </button>
                    <button onClick={() => setCopyModalState({ isOpen: true, text: whiteLabelUrl, title: "Enlace para Cliente" })} className="w-full flex items-center justify-center gap-2 py-3 bg-white border border-gray-300 text-gray-700 font-bold rounded-xl hover:bg-gray-100 text-xs shadow-sm">
                      <LinkIcon className="w-4 h-4 text-sky-600" /> Copiar Enlace
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <button onClick={() => setView('email')} className="w-full flex items-center justify-center gap-2 py-3 bg-[#0073b7] text-white font-bold rounded-xl shadow-md hover:opacity-90 text-xs">
                    <EnvelopeIcon className="w-4 h-4" /> Enviar por Correo
                  </button>

                  <div className="grid grid-cols-2 gap-2">
                    <button onClick={() => setIsTechnicalSheetOpen(true)} className="flex items-center justify-center gap-2 py-2.5 bg-red-50 text-red-700 hover:bg-red-100 border border-red-200 font-bold rounded-xl text-xs transition-colors">
                      <DocumentArrowDownIcon className="w-4 h-4" /> Ficha Técnica
                    </button>
                    <button onClick={handleDownloadZip} disabled={zipStatus === 'processing' || !property.images?.length} className="flex items-center justify-center gap-2 py-2.5 bg-amber-50 text-amber-700 border border-amber-100 font-bold rounded-xl text-xs">
                      {zipStatus === 'processing' ? <SpinnerIcon className="w-4 h-4" /> : <PhotoIcon className="w-4 h-4" />} Bajar Fotos
                    </button>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <>
              <header className="relative text-center mb-6">
                <button onClick={() => setView('options')} className="absolute left-0 top-1/2 -translate-y-1/2 p-2 hover:bg-gray-100 rounded-full"><ChevronLeftIcon className="w-5 h-5 text-gray-600" /></button>
                <h2 className="text-lg font-bold text-gray-800">Enviar por Email</h2>
              </header>
              {emailSendStatus === 'success' ? (
                <div className="text-center py-6">
                  <CheckCircleIcon className="w-14 h-14 text-green-500 mx-auto mb-3" />
                  <p className="font-bold text-gray-800 text-sm">¡Enviado con éxito!</p>
                  <button onClick={onClose} className="mt-4 text-sky-600 font-bold uppercase text-xs">Listo</button>
                </div>
              ) : (
                <form onSubmit={handleSendEmail} className="space-y-4">
                  <input type="email" value={recipientEmail} onChange={(e) => setRecipientEmail(e.target.value)} placeholder="Correo del cliente" required className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm outline-none" />
                  {emailError && <p className="text-xs text-red-500">{emailError}</p>}
                  <button type="submit" disabled={emailSendStatus === 'sending'} className="w-full py-3.5 bg-sky-600 text-white font-bold rounded-xl flex items-center justify-center gap-2 text-sm">
                    {emailSendStatus === 'sending' ? <SpinnerIcon className="w-5 h-5" /> : 'Enviar ahora'}
                  </button>
                </form>
              )}
            </>
          )}
        </div>
      </div>
      {copyModalState?.isOpen && <CopyFallbackModal isOpen={copyModalState.isOpen} onClose={() => setCopyModalState(null)} textToCopy={copyModalState.text} title={copyModalState.title} />}
      {isTechnicalSheetOpen && property && (
        <PropertyTechnicalSheetModal
          isOpen={isTechnicalSheetOpen}
          onClose={() => setIsTechnicalSheetOpen(false)}
          property={property}
          appUser={appUser}
        />
      )}
    </>
  );
};
